import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Modal, Table, Tag, Descriptions, Card, Row, Col, Avatar, Button, message, Space, Tooltip, Checkbox } from 'antd'
import { UserOutlined, CheckCircleOutlined, EyeOutlined, ClockCircleOutlined, MailOutlined, DownloadOutlined } from '@ant-design/icons'
import {
  downloadSignedTbtPdf,
  downloadSelectedSubcontractorsPdf,
  updateTbtRunStatus,
  sendTbtReminder
} from '../../../../HttpRequests/toolboxTalks'

export default class RunDetailsModal extends React.PureComponent {

  static propTypes = {
    visible: PropTypes.bool.isRequired,
    run: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onRunUpdated: PropTypes.func,
    onRefreshRunDetails: PropTypes.func,
  }

  state = {
    selectedRecipients: [],
    countdownTimers: {},
    downloading: false,
    unsending: false,
    sending: false,
    sendingReminder: false
  }

  componentDidMount() {
    this.startCountdownTimers()
  }

  componentWillUnmount() {
    this.clearCountdownTimers()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.run !== this.props.run) {
      this.clearCountdownTimers()
      this.startCountdownTimers()
    }
  }

  startCountdownTimers = () => {
    const { run } = this.props
    if (!run || !run.recipients) return

    run.recipients.forEach(record => {
      if (record.email && record.email.status === 'pending' && record.countdown_minutes > 0) {
        const intervalId = setInterval(() => {
          this.setState(prevState => {
            const newCountdown = Math.max(0, (prevState.countdownTimers[record.id] || record.countdown_minutes) - 1)
            return {
              countdownTimers: {
                ...prevState.countdownTimers,
                [record.id]: newCountdown
              }
            }
          })
        }, 60000) // Update every minute

        this.setState(prevState => ({
          countdownTimers: {
            ...prevState.countdownTimers,
            [record.id]: record.countdown_minutes,
            [`interval_${record.id}`]: intervalId
          }
        }))
      }
    })
  }

  clearCountdownTimers = () => {
    const { countdownTimers } = this.state
    Object.keys(countdownTimers).forEach(key => {
      if (key.startsWith('interval_')) {
        clearInterval(countdownTimers[key])
      }
    })
    this.setState({ countdownTimers: {} })
  }

  getStatusIcon = (record) => {
    // Priority order: Signed > Viewed > Hold > Sent > Pending
    if (record.signed_at) {
      return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
    }
    if (record.viewed_at) {
      return <EyeOutlined style={{ color: '#1890ff', fontSize: 16 }} />
    }
    if (record.email && record.email.status === 'hold') {
      return <ClockCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
    }
    if (record.email && (record.email.status === 'sent' || record.email.status === 'delivered')) {
      return <MailOutlined style={{ color: '#1890ff', fontSize: 16 }} />
    }
    if (record.email && record.email.status === 'pending') {
      return <ClockCircleOutlined style={{ color: '#faad14', fontSize: 16 }} />
    }
    return <ClockCircleOutlined style={{ color: '#d9d9d9', fontSize: 16 }} />
  }

  getStatusText = (record) => {
    const { countdownTimers } = this.state

    // Priority order: Signed > Viewed > Hold > Sent > Pending
    if (record.signed_at) {
      return <Tag color="success">Signed</Tag>
    }
    if (record.viewed_at) {
      return <Tag color="processing">Viewed</Tag>
    }
    if (record.email && record.email.status === 'hold') {
      return <Tag color="error">On Hold</Tag>
    }
    if (record.email && (record.email.status === 'sent' || record.email.status === 'delivered')) {
      return <Tag color="blue">Sent</Tag>
    }
    if (record.email && record.email.status === 'pending') {
      const countdown = countdownTimers[record.id] || record.countdown_minutes || 0
      if (countdown > 0) {
        return <Tag color="warning">Pending ({countdown}m)</Tag>
      }
      return <Tag color="warning">Pending</Tag>
    }
    return <Tag color="default">Pending</Tag>
  }

  handleDownloadPdf = async (record) => {
    try {
      message.loading({ content: 'Generating PDF download...', key: 'pdf-download' })

      const response = await downloadSignedTbtPdf({ signedId: record.id })

      if (response.data && response.data.download_url) {
        // Create a temporary link to download the file
        const link = document.createElement('a')
        link.href = response.data.download_url
        link.download = response.data.filename || 'signed_tbt.pdf'
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        message.success({ content: 'PDF download started', key: 'pdf-download' })
      } else {
        throw new Error('No download URL received')
      }
    } catch (error) {
      console.error('PDF download error:', error)
      const errorMessage = (error.response && error.response.data && error.response.data.message)
        ? error.response.data.message
        : 'Failed to download PDF'
      message.error({
        content: errorMessage,
        key: 'pdf-download'
      })
    }
  }

  handleRecipientSelection = (recordId, checked) => {
    const { selectedRecipients } = this.state
    if (checked) {
      this.setState({
        selectedRecipients: [...selectedRecipients, recordId]
      })
    } else {
      this.setState({
        selectedRecipients: selectedRecipients.filter(id => id !== recordId)
      })
    }
  }

  handleSelectAll = (checked) => {
    const { run } = this.props
    if (checked) {
      const allRecipientIds = (run.recipients || []).map(record => record.id)
      this.setState({ selectedRecipients: allRecipientIds })
    } else {
      this.setState({ selectedRecipients: [] })
    }
  }

  handleUnsendTBT = async () => {
    const { run, onRunUpdated } = this.props;
    const { selectedRecipients } = this.state;

    if (selectedRecipients.length === 0) {
      message.warning('Please select recipients to unsend TBT');
      return;
    }

    // Filter out recipients that can't be unsent
    const unsendableRecipients = (run.recipients || [])
      .filter(record => selectedRecipients.includes(record.id) && record.can_unsend);

    if (unsendableRecipients.length === 0) {
      message.warning('No selected recipients can be unsent');
      return;
    }

    this.setState({ unsending: true });
    const hide = message.loading('Updating TBT status...', 0);

    try {
      const response = await updateTbtRunStatus(
        run.id,
        unsendableRecipients.map(r => r.subcontractor_id),
        'hold'
      );

      // Update the UI to reflect the changes
      if (onRunUpdated) {
        onRunUpdated();
      }

      // Refresh the run details to show updated recipient status
      if (this.props.onRefreshRunDetails) {
        this.props.onRefreshRunDetails();
      }

      message.success(`Successfully updated status for ${unsendableRecipients.length} recipient(s) to "On Hold"`);

      // Clear selection after successful update
      this.setState({ selectedRecipients: [] });

    } catch (error) {
      console.error('Failed to update TBT status:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Failed to update TBT status';
      message.error(errorMessage);
    } finally {
      hide();
      this.setState({ unsending: false });
    }
  }

  handleSendTBT = async () => {
    const { run, onRunUpdated } = this.props;
    const { selectedRecipients } = this.state;

    if (selectedRecipients.length === 0) {
      message.warning('Please select recipients to send TBT');
      return;
    }

    // Filter out recipients that can't be sent (only hold emails can be sent)
    const sendableRecipients = (run.recipients || [])
      .filter(record => selectedRecipients.includes(record.id) && record.can_send);

    if (sendableRecipients.length === 0) {
      message.warning('No selected recipients can be sent');
      return;
    }

    this.setState({ sending: true });
    const hide = message.loading('Updating TBT status...', 0);

    try {
      const response = await updateTbtRunStatus(
        run.id,
        sendableRecipients.map(r => r.subcontractor_id),
        'pending'
      );

      // Update the UI to reflect the changes
      if (onRunUpdated) {
        onRunUpdated();
      }

      // Refresh the run details to show updated recipient status
      if (this.props.onRefreshRunDetails) {
        this.props.onRefreshRunDetails();
      }

      message.success(`Successfully updated status for ${sendableRecipients.length} recipient(s) to "Pending"`);

      // Clear selection after successful update
      this.setState({ selectedRecipients: [] });

    } catch (error) {
      console.error('Failed to update TBT status:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Failed to update TBT status';
      message.error(errorMessage);
    } finally {
      hide();
      this.setState({ sending: false });
    }
  }

  handleSendReminder = async () => {
    const { run, onRunUpdated } = this.props;
    const { selectedRecipients } = this.state;

    if (selectedRecipients.length === 0) {
      message.warning('Please select recipients to send reminder');
      return;
    }

    // Filter recipients that can receive reminders (sent but not signed)
    const remindableRecipients = (run.recipients || [])
      .filter(record => {
        if (!selectedRecipients.includes(record.id)) return false;
        // Must have email sent and not signed
        const emailSent = record.email && (record.email.status === 'sent' || record.email.status === 'delivered');
        const notSigned = !record.signed_at;
        return emailSent && notSigned;
      });

    if (remindableRecipients.length === 0) {
      message.warning('No selected recipients can receive reminders (must be sent but not signed)');
      return;
    }

    this.setState({ sendingReminder: true });
    const hide = message.loading('Sending reminders...', 0);

    try {
      const response = await sendTbtReminder(
        run.id,
        remindableRecipients.map(r => r.subcontractor_id)
      );

      // Update the UI to reflect the changes
      if (onRunUpdated) {
        onRunUpdated();
      }

      // Refresh the run details
      if (this.props.onRefreshRunDetails) {
        this.props.onRefreshRunDetails();
      }

      message.success(`Successfully sent reminders to ${remindableRecipients.length} recipient(s)`);

      // Clear selection after successful send
      this.setState({ selectedRecipients: [] });

    } catch (error) {
      console.error('Failed to send reminder:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Failed to send reminder';
      message.error(errorMessage);
    } finally {
      hide();
      this.setState({ sendingReminder: false });
    }
  }

  render() {
    const { visible, run, onClose } = this.props

    if (!run) return null

    const { selectedRecipients } = this.state
    const recipients = run.recipients || []
    const allSelected = recipients.length > 0 && selectedRecipients.length === recipients.length

    const recipientColumns = [
      {
        title: 'Subcontractor',
        key: 'subcontractor',
        width: 200,
        render: (text, record) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              size="small"
              icon={<UserOutlined />}
              style={{ marginRight: 8, backgroundColor: '#1890ff' }}
            />
            <div>
              <div style={{ fontWeight: 500 }}>{record.subcontractor_name}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>{record.subcontractor_email}</div>
            </div>
          </div>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        width: 120,
        render: (text, record) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {this.getStatusIcon(record)}
            <span style={{ marginLeft: 8 }}>{this.getStatusText(record)}</span>
          </div>
        ),
      },
      {
        title: 'Sent',
        key: 'sent_at',
        width: 150,
        render: (text, record) => {
          // Show email sent time if email was sent, otherwise show '-'
          if (record.email && (record.email.status === 'sent' || record.email.status === 'delivered')) {
            return record.email.sent_at ? moment(record.email.sent_at).format('DD MMM HH:mm') : '-'
          }
          return '-'
        },
      },
      {
        title: 'Viewed',
        key: 'viewed_at',
        width: 150,
        render: (text, record) => record.viewed_at ? moment(record.viewed_at).format('DD MMM HH:mm') : '-',
      },
      {
        title: 'Signed',
        key: 'signed_at',
        width: 150,
        render: (text, record) => record.signed_at ? moment(record.signed_at).format('DD MMM HH:mm') : '-',
      },
      {
        title: 'IP Address',
        key: 'ip_address',
        width: 120,
        render: (text, record) => {
          const ip = record.ip_address
          // Display '-' for localhost IPs or empty values
          if (!ip || ip === '::1' || ip === '127.0.0.1' || ip === 'localhost') {
            return '-'
          }
          return ip
        },
      },
      {
        title: (
          <Space>
            <span>Actions</span>
            <Checkbox
              checked={allSelected}
              indeterminate={selectedRecipients.length > 0 && selectedRecipients.length < recipients.length}
              onChange={(e) => this.handleSelectAll(e.target.checked)}
            />
          </Space>
        ),
        key: 'actions',
        width: 120,
        render: (text, record) => (
          <Space>
            {record.signed_at ? (
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => this.handleDownloadPdf(record)}
                title="Download Signed PDF"
              >
                PDF
              </Button>
            ) : (
              <span>-</span>
            )}
            <Checkbox
              checked={selectedRecipients.includes(record.id)}
              onChange={(e) => this.handleRecipientSelection(record.id, e.target.checked)}
            />
          </Space>
        ),
      },
    ]

    const documentColumns = [
      {
        title: 'Document',
        key: 'title',
        render: (text, record) => (
          <div>
            <div style={{ fontWeight: 500 }}>{record.title}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.file_type ? record.file_type.toUpperCase() : ''}</div>
          </div>
        ),
      },
      {
        title: 'File Size',
        key: 'file_size',
        width: 100,
        render: (text, record) => {
          if (!record.file_size) return '-'
          const size = record.file_size
          if (size < 1024) return `${size} B`
          if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
          return `${Math.round(size / (1024 * 1024))} MB`
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => {
          const teamSlug = window.location.pathname.split('/')[3] // Extract team from URL
          return (
            <Space>
              <Tooltip title="View Document">
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    window.open(`/exports/teams/${teamSlug}/toolbox-talks/${record.id}/view`, '_blank')
                  }}
                />
              </Tooltip>
              <Tooltip title="Download Document">
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    const link = document.createElement('a')
                    link.href = `/exports/teams/${teamSlug}/toolbox-talks/${record.id}/download`
                    link.download = `${record.title}.${record.file_type}`
                    link.click()
                  }}
                />
              </Tooltip>
            </Space>
          )
        },
      },
    ]

    return (
      <Modal
        title={`TBT Run #${run.id} Details`}
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={1000}
        bodyStyle={{ padding: '20px' }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Run Information" size="small">
              <Descriptions column={3} size="small">
                <Descriptions.Item label="Status">
                  {(() => {
                    const status = run.actual_status || run.status
                    switch (status) {
                      case 'fully_completed':
                        return <Tag color="success">Fully Completed</Tag>
                      case 'partially_completed':
                        return <Tag color="processing">Partially Completed</Tag>
                      case 'sending':
                        return <Tag color="warning">Sending Emails</Tag>
                      case 'sent':
                        return <Tag color="blue">Emails Sent</Tag>
                      case 'pending_emails':
                        return <Tag color="warning">Pending Emails</Tag>
                      case 'on_hold':
                        return <Tag color="error">On Hold</Tag>
                      case 'completed':
                        return <Tag color="success">Distribution Complete</Tag>
                      case 'running':
                        return <Tag color="processing">Running</Tag>
                      case 'pending':
                        return <Tag color="warning">Pending</Tag>
                      case 'failed':
                        return <Tag color="error">Failed</Tag>
                      default:
                        return <Tag color="default">{status}</Tag>
                    }
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {moment(run.created_at).format('DD MMM YYYY HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label="Duration">
                  {run.completed_at && run.started_at
                    ? `${moment(run.completed_at).diff(moment(run.started_at), 'minutes')} minutes`
                    : run.status === 'running'
                      ? `${moment().diff(moment(run.started_at), 'minutes')} minutes (running)`
                      : '-'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Documents">
                  {run.documents_count} document{run.documents_count !== 1 ? 's' : ''}
                </Descriptions.Item>
                <Descriptions.Item label="Recipients">
                  {run.total_recipients} subcontractor{run.total_recipients !== 1 ? 's' : ''}
                </Descriptions.Item>
                <Descriptions.Item label="Completion">
                  {run.signed_count || 0} signed, {run.viewed_count || 0} viewed
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {run.documents && run.documents.length > 0 && (
            <Col span={24}>
              <Card title="Documents Distributed" size="small">
                <Table
                  dataSource={run.documents}
                  columns={documentColumns}
                  rowKey={record => record.id}
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          )}

          <Col span={24}>
            <Card title="Recipients" size="small">
              <Table
                dataSource={run.recipients || []}
                columns={recipientColumns}
                rowKey={record => record.id}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  size: 'small',
                }}
                size="small"
                scroll={{ x: 800 }}
              />
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Space>
                  {(() => {
                    const selectedUnsendableRecipients = (run.recipients || [])
                      .filter(record => selectedRecipients.includes(record.id) && record.can_unsend)
                    const canUnsendAny = selectedUnsendableRecipients.length > 0

                    return (
                      <Button
                        type="primary"
                        danger
                        loading={this.state.unsending}
                        onClick={this.handleUnsendTBT}
                        disabled={selectedRecipients.length === 0 || !canUnsendAny}
                      >
                        Unsend TBT ({selectedUnsendableRecipients.length}/{selectedRecipients.length})
                      </Button>
                    )
                  })()}

                  {(() => {
                    const selectedSendableRecipients = (run.recipients || [])
                      .filter(record => selectedRecipients.includes(record.id) && record.can_send)
                    const canSendAny = selectedSendableRecipients.length > 0

                    return (
                      <Button
                        type="primary"
                        loading={this.state.sending}
                        onClick={this.handleSendTBT}
                        disabled={selectedRecipients.length === 0 || !canSendAny}
                      >
                        Send TBT ({selectedSendableRecipients.length}/{selectedRecipients.length})
                      </Button>
                    )
                  })()}

                  {(() => {
                    const selectedRemindableRecipients = (run.recipients || [])
                      .filter(record => {
                        if (!selectedRecipients.includes(record.id)) return false;
                        const emailSent = record.email && (record.email.status === 'sent' || record.email.status === 'delivered');
                        const notSigned = !record.signed_at;
                        return emailSent && notSigned;
                      })
                    const canRemindAny = selectedRemindableRecipients.length > 0

                    return (
                      <Button
                        type="default"
                        loading={this.state.sendingReminder}
                        onClick={this.handleSendReminder}
                        disabled={selectedRecipients.length === 0 || !canRemindAny}
                      >
                        Send Reminder ({selectedRemindableRecipients.length}/{selectedRecipients.length})
                      </Button>
                    )
                  })()}

                  <Button
                    type="primary"
                    loading={this.state.downloading}
                    onClick={async () => {
                      const selectedSubcontractorIds = selectedRecipients
                        .map(id => {
                          const recipient = (run.recipients || []).find(r => r.id === id);
                          return recipient && recipient.subcontractor_id;
                        })
                        .filter(id => id != null);

                      if (selectedSubcontractorIds.length === 0) {
                        message.warning('No subcontractors selected for report');
                        return;
                      }

                      this.setState({ downloading: true });

                      try {
                        const response = await downloadSelectedSubcontractorsPdf(run.id, selectedSubcontractorIds);

                        // Create blob and download
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `TBT_Selected_Subcontractors_Run_${run.id}_${run.run_at ? run.run_at.split('T')[0] : new Date().toISOString().split('T')[0]}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);

                        message.success('TBT report downloaded successfully');
                      } catch (error) {
                        console.error('Failed to download TBT PDF:', error);
                        message.error('Failed to download TBT report');
                      } finally {
                        this.setState({ downloading: false });
                      }
                    }}
                    disabled={selectedRecipients.length === 0}
                    icon={<DownloadOutlined />}
                  >
                    Download Report
                  </Button>
                </Space>
              </div>
            </Card>
          </Col>
        </Row>
      </Modal>
    )
  }
}
